import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiError, errorMessage, get } from '../../api.js';
import { Notice, formatDateTime } from '../../components/ui.js';
import { useSignedIn } from '../../session.js';
import type { Page } from './types.js';

interface AuditItem {
  id: string;
  occurredAt: string;
  action: string;
  actor: { id: string; name: string | null } | null;
  entity: { type: string; id: string | null; name: string | null } | null;
  details: Record<string, unknown>;
}

/** What each action means, in plain words. */
const ACTIONS: Record<string, string> = {
  'auth.sign_in': 'Signed in',
  'auth.sign_in_failed': 'Sign-in refused',
  'auth.code_failed': 'Wrong code',
  'auth.locked': 'Account locked',
  'auth.recovery_code_used': 'Recovery code used',
  'auth.app_set_up': 'Authenticator app set up',
  'auth.recovery_codes_regenerated': 'New recovery codes',
  'auth.sign_out': 'Signed out',
  'auth.invitation_accepted': 'Invitation accepted',
  'auth.password_reset_requested': 'Password reset asked',
  'auth.password_reset': 'Password reset',
  'auth.password_changed': 'Password changed',
  'auth.trusted_device_added': 'Device trusted',
  'auth.trusted_device_removed': 'Trusted device removed',
  'admin.user_created': 'User created',
  'admin.invitation_sent': 'Invitation sent',
  'admin.user_updated': 'User changed',
  'admin.roles_changed': 'Roles changed',
  'admin.user_deactivated': 'User deactivated',
  'admin.user_reactivated': 'User reactivated',
  'admin.user_unlocked': 'User unlocked',
  'admin.app_reset': 'Authenticator app reset',
  'admin.trusted_devices_revoked': 'Trusted devices forgotten',
  'system.initial_admin_invited': 'First administrator invited',
  'contact.created': 'Contact created',
  'contact.updated': 'Contact changed',
  'contact.deleted': 'Contact deleted',
  'contact.viewed': 'Contact sheet opened',
  'lead.created': 'Lead created',
  'lead.updated': 'Lead changed',
  'lead.deleted': 'Lead deleted',
  'lead.viewed': 'Lead sheet opened',
  'lead.contact_linked': 'Contact linked to a lead',
  'lead.contact_unlinked': 'Contact removed from a lead',
  'event.created': 'Event created',
  'event.updated': 'Event changed',
  'event_note.created': 'Event note added',
  'event_note.updated': 'Event note changed',
  'event_note.deleted': 'Event note deleted',
  'contact.card_uploaded': 'Card photo received',
  'contact.card_viewed': 'Card photo opened',
  'contact.read_by_ai': 'Card read by AI',
  'contact.enriched': 'Contact completed by web search',
  'system.audit_purged': 'Audit entries over 24 months removed',
  'admin.kiosk_pin_set': 'Kiosk PIN set',
  'contact.imported': 'Contact imported from HubSpot',
  'admin.hubspot_retry': 'HubSpot: failed records sent again',
  'roi.project_created': 'ROI project created',
  'roi.project_updated': 'ROI project changed',
  'roi.project_deleted': 'ROI project deleted',
  'roi.project_duplicated': 'ROI project duplicated',
  'roi.project_restored': 'ROI project restored',
  'roi.project_purged': 'ROI project removed for good',
  'admin.hubspot_fill_missing': 'HubSpot: missing fields filled on synced records',
  'contact.company_archived_in_hubspot': 'Company deleted in HubSpot with its last contact',
  'lead.company_archived_in_hubspot': 'Company deleted in HubSpot with its last deal',
};

const FILTERS = [
  { value: '', label: 'All actions' },
  { value: 'auth.', label: 'Sign-in' },
  { value: 'admin.', label: 'Administration' },
  { value: 'contact.', label: 'Contacts' },
  { value: 'lead.', label: 'Leads' },
  { value: 'event', label: 'Events' },
  { value: 'roi.', label: 'ROI' },
];

function describe(details: Record<string, unknown>): string {
  return Object.entries(details)
    .map(
      ([key, value]) =>
        `${key}: ${Array.isArray(value) ? value.join(', ') || 'none' : String(value)}`,
    )
    .join(' · ');
}

/** Administration > Audit log: newest first, pages of 20. */
export function Audit() {
  const { refresh } = useSignedIn();
  const [params, setParams] = useSearchParams();
  const action = params.get('action') ?? '';
  const userId = params.get('userId') ?? '';
  const page = Number(params.get('page') ?? '1') || 1;
  const [data, setData] = useState<Page<AuditItem> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const query = new URLSearchParams({ page: String(page) });
    if (action) query.set('action', action);
    if (userId) query.set('userId', userId);
    let current = true;
    get<Page<AuditItem>>(`/admin/audit?${query.toString()}`)
      .then((res) => current && setData(res))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [action, userId, page, refresh]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  return (
    <>
      <Link to="/settings" className="back">
        Settings
      </Link>
      <h1 className="page">Audit log</h1>
      <p className="lede">
        Every sign-in, administration action and change to contacts, leads and events, and every
        opening of a contact or lead sheet, kept 24 months. It holds people by their account, never
        their email or phone, and the names of the changed fields, never their values.
      </p>
      <div className="toolbar">
        <select aria-label="Action" value={action} onChange={(e) => set('action', e.target.value)}>
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {userId && (
          <button type="button" className="btn" onClick={() => set('userId', '')}>
            Everyone
          </button>
        )}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {data &&
        (data.items.length === 0 ? (
          <div className="empty">
            <strong>Nothing yet</strong>
          </div>
        ) : (
          <ul className="list" aria-label="Audit entries">
            {data.items.map((item) => (
              <li key={item.id} className="row static">
                <span className="tx">
                  <span className="t1">{ACTIONS[item.action] ?? item.action}</span>
                  <span className="t2">
                    {item.actor ? `By ${item.actor.name ?? 'a deleted user'}` : 'By the portal'}
                    {item.entity?.type === 'user' && item.entity.id !== item.actor?.id
                      ? `, for ${item.entity.name ?? 'an unknown account'}`
                      : ''}
                    {Object.keys(item.details).length > 0 ? ` · ${describe(item.details)}` : ''}
                  </span>
                </span>
                <span className="rt">{formatDateTime(item.occurredAt)}</span>
              </li>
            ))}
          </ul>
        ))}
      {data && pages > 1 && (
        <nav className="pager" aria-label="Pages">
          <button
            type="button"
            className="btn sm"
            disabled={page <= 1}
            onClick={() => set('page', String(page - 1))}
          >
            Previous
          </button>
          <span>
            Page {page} of {pages}
          </span>
          <button
            type="button"
            className="btn sm"
            disabled={page >= pages}
            onClick={() => set('page', String(page + 1))}
          >
            Next
          </button>
        </nav>
      )}
    </>
  );
}
